/**
 * AI Provider Config Panel — Cockpit CFO multi-provider.
 *
 * Permet à un admin de configurer la clé API pour l'analyse financière IA :
 * - OpenAI (gpt-4o, gpt-4o-mini, gpt-5.2)
 * - Anthropic Claude (sonnet 4.5, opus 4.5, haiku 4.5)
 * - Google Gemini (3-pro, 3-flash)
 *
 * Sécurité :
 * - La clé n'est JAMAIS retournée en clair (seulement masquée `sk-***xyz`)
 * - axios injecte automatiquement le CSRF token (intercepteur central)
 * - Bouton "Tester" pour valider live avant Save
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Activity, CheckCircle2, AlertCircle, Eye, EyeOff,
  Loader2, Save, Trash2, ShieldCheck,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AiConfigPanel() {
  const { toast } = useToast();
  const [providers, setProviders] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // Form
  const [provider, setProvider] = useState('openai');
  const [model, setModel] = useState('gpt-4o');
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);

  // Last test result
  const [testResult, setTestResult] = useState(null);

  // Charger config + providers
  const fetchAll = async () => {
    setLoading(true);
    try {
      const [pr, cur] = await Promise.all([
        axios.get(`${API}/admin/ai-config/providers`),
        axios.get(`${API}/admin/ai-config`),
      ]);
      setProviders(pr.data?.providers || []);
      setCurrent(cur.data);
      if (cur.data?.provider) {
        setProvider(cur.data.provider);
        setModel(cur.data.model);
      }
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Models disponibles pour le provider sélectionné
  const currentProvider = providers.find(p => p.id === provider);
  const availableModels = currentProvider?.models || [];

  // Quand le provider change, choisir le default_model
  useEffect(() => {
    if (currentProvider?.default_model && !availableModels.includes(model)) {
      setModel(currentProvider.default_model);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [provider, currentProvider]);

  const handleTest = async () => {
    if (!apiKey || apiKey.length < 8) {
      toast({ title: 'Clé invalide', description: 'Renseignez d\'abord la clé API.', variant: 'destructive' });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const r = await axios.post(`${API}/admin/ai-config/validate`, { provider, model, apiKey });
      setTestResult(r.data);
      if (r.data?.ok) {
        toast({ title: '✅ Clé valide', description: `Latence ${r.data.latencyMs}ms` });
      } else {
        toast({ title: '❌ Clé invalide', description: r.data?.error || 'Validation échouée', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!apiKey || apiKey.length < 8) {
      toast({ title: 'Clé invalide', description: 'Renseignez la clé API.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/ai-config`, {
        provider, model, apiKey,
        fallbackToEmergent: false,
      });
      toast({ title: '✅ Sauvegardé', description: `${currentProvider?.label} • ${model}` });
      setApiKey('');  // Clear le champ après save pour la sécurité
      await fetchAll();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Supprimer la clé API ? Les insights IA du Cockpit CFO seront désactivés tant qu\'une nouvelle clé n\'est pas configurée.')) return;
    try {
      await axios.delete(`${API}/admin/ai-config`);
      toast({ title: 'Config supprimée', description: 'Aucune clé API active.' });
      setApiKey('');
      await fetchAll();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="ai-config-panel">
      {/* État actuel */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="w-5 h-5 text-indigo-500" />
            État actuel de la configuration IA
          </CardTitle>
        </CardHeader>
        <CardContent>
          {current?.configured ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="ai-config-status">
              <StatItem label="Provider" value={current.provider} icon={Activity} color="text-indigo-600" />
              <StatItem label="Modèle" value={current.model} />
              <StatItem label="Clé API" value={current.apiKeyMasked} mono />
              {current.updatedAt && (
                <div className="col-span-full text-[11px] text-gray-400">
                  Dernière modif : {new Date(current.updatedAt).toLocaleString('fr-FR')}
                  {current.updatedBy ? ` par ${current.updatedBy}` : ''}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3 text-sm text-gray-600" data-testid="ai-config-not-configured">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Aucune clé API configurée. Les insights IA du Cockpit CFO sont désactivés tant qu'une clé OpenAI, Claude ou Gemini n'est pas renseignée ci-dessous.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Formulaire */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="w-5 h-5 text-emerald-500" />
            Configurer une clé API custom
          </CardTitle>
          <p className="text-xs text-gray-500 mt-1">
            La clé est chiffrée AES (Fernet) avant stockage. Elle n'est jamais visible en clair côté UI ni dans les logs.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Provider */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Provider</Label>
              <Select value={provider} onValueChange={setProvider}>
                <SelectTrigger data-testid="ai-provider-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {providers.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Modèle</Label>
              <Select value={model} onValueChange={setModel}>
                <SelectTrigger data-testid="ai-model-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableModels.map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* API Key */}
          <div>
            <Label className="text-xs">Clé API ({currentProvider?.key_prefix_hint || '...'})</Label>
            <div className="relative">
              <Input
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={current?.configured ? 'Laissez vide pour conserver la clé actuelle (sinon écrasement)' : `${currentProvider?.key_prefix_hint || ''}`}
                className="font-mono text-sm pr-10"
                data-testid="ai-api-key-input"
              />
              <button type="button" onClick={() => setShowKey(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {testResult && (
              <div className={`mt-2 text-xs flex items-center gap-2 ${testResult.ok ? 'text-emerald-600' : 'text-red-600'}`}>
                {testResult.ok
                  ? <><CheckCircle2 className="w-4 h-4" /> Clé valide (latence {testResult.latencyMs}ms)</>
                  : <><AlertCircle className="w-4 h-4" /> {testResult.error || 'Échec validation'}</>
                }
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={handleTest} disabled={!apiKey || testing} data-testid="ai-test-btn">
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Tester la clé
            </Button>
            <Button size="sm" onClick={handleSave} disabled={!apiKey || saving} data-testid="ai-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {current?.configured ? 'Mettre à jour' : 'Enregistrer'}
            </Button>
            {current?.configured && (
              <Button variant="ghost" size="sm" onClick={handleReset} data-testid="ai-reset-btn"
                className="text-red-600 hover:text-red-700 ml-auto">
                <Trash2 className="w-4 h-4 mr-2" /> Supprimer la config custom
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Aide */}
      <Card className="bg-blue-50/50 border-blue-100">
        <CardContent className="pt-5 text-xs text-gray-700 space-y-2">
          <p className="font-semibold text-blue-900">💡 Où obtenir une clé API ?</p>
          <ul className="space-y-1 ml-4 list-disc">
            <li><b>OpenAI</b> : <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">platform.openai.com/api-keys</a> (créer une clé secrète)</li>
            <li><b>Anthropic Claude</b> : <a href="https://console.anthropic.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">console.anthropic.com</a> → Settings → API Keys</li>
            <li><b>Google Gemini</b> : <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">aistudio.google.com/app/apikey</a></li>
          </ul>
          <p className="mt-2 text-[11px] text-gray-500">
            La clé est utilisée pour générer les insights financiers du Cockpit CFO (synthèse, tendances, risques, projection 90j).
            Coût estimé : 0.01€ par génération avec gpt-4o-mini ou claude-haiku.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function StatItem({ label, value, icon: Icon, color = 'text-gray-700', mono = false }) {
  return (
    <div className="bg-gray-50 border border-gray-100 rounded-md p-3">
      <p className="text-[10px] text-gray-500 uppercase tracking-wide">{label}</p>
      <div className="flex items-center gap-2 mt-1">
        {Icon && <Icon className={`w-4 h-4 ${color}`} />}
        <p className={`text-sm font-medium ${color} ${mono ? 'font-mono' : ''}`}>{value || '—'}</p>
      </div>
    </div>
  );
}
